import { Link } from "react-router-dom";
import { Copy } from "lucide-react";
import { Button, buttonClass, useToast } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { CreatedSellerCredentials } from "./SellerCreateForm";
import { copyToClipboard } from "./helpers";

/**
 * The new seller's temporary login, shown once. Nothing stores it: once the
 * drawer closes it is gone, which is why the copy button leads.
 */
export function CredentialsPanel({
  credentials,
  locale,
}: {
  credentials: CreatedSellerCredentials;
  locale: string;
}) {
  const t = useTranslations();
  const toast = useToast();

  return (
    <>
      <p style={{ margin: 0, fontSize: 13, color: "var(--rd-text-secondary)" }}>
        {t(keys.admin.temporaryCredentialsHint)}
      </p>
      {/* Labels keep the page direction; each VALUE is its own LTR isolate. */}
      <dl
        className="rd-kv"
        style={{
          padding: "12px 14px",
          borderRadius: 8,
          background: "var(--rd-surface-soft)",
          boxShadow: "inset 0 0 0 1px var(--rd-line-header)",
        }}
      >
        <dt>{t(keys.common.phone)}</dt>
        <dd className="rd-num" style={{ textAlign: "start" }}>
          <bdi dir="ltr">{credentials.phone}</bdi>
        </dd>
        <dt>{t(keys.common.password)}</dt>
        <dd style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
          <bdi dir="ltr">{credentials.temporaryPassword}</bdi>
        </dd>
      </dl>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Button
          variant="primary"
          size="sm"
          icon={Copy}
          onClick={() => {
            void copyToClipboard(
              `${credentials.phone}\n${credentials.temporaryPassword}`,
            ).then((copied) => {
              if (copied) toast.show({ tone: "neutral", icon: Copy, message: t("admin.loginDetailsCopied") });
            });
          }}
        >
          {t(keys.admin.copyCredentials)}
        </Button>
        <Link
          to={`/${locale}/admin/sellers/${credentials.sellerId}`}
          className={buttonClass("ghost", "sm")}
          style={{ textDecoration: "none" }}
        >
          {t(keys.common.view)}
        </Link>
        <Link
          to={`/${locale}/admin/stores/${credentials.storeId}`}
          className={buttonClass("ghost", "sm")}
          style={{ textDecoration: "none" }}
        >
          {t(keys.admin.viewCreatedBusiness)}
        </Link>
      </div>
    </>
  );
}
